<script lang="ts">
  /**
   * DEALER POSITIONING — the GEX-by-strike ladder (plan §13, dealer rail).
   *
   * SVG, not canvas, and that is a decision rather than a preference: an SVG inherits CSS, so every
   * mark reads the design tokens directly and a theme flip repaints it for free. The chart needs its
   * canvas bridge (`theme.ts`) precisely because canvas cannot do this; a ladder of ~25 bars has no
   * reason to take on that failure mode.
   *
   * TWO MEASURED CONSTRAINTS SHAPE THIS PANEL (docs/design/PANEL_DATA_CONTRACTS_2026-08-06.md):
   *   D-3  the GEX feed is NOT root-separated — SPX and SPXW return byte-identical payloads. So this
   *        panel has NO root toggle. The mock's SPX|SPXW control would have changed the label and
   *        nothing else, which is worse than no control at all.
   *   D-4  the age caveat is DERIVED FROM THE PAYLOAD, never hardcoded. When `vendor_ts` is absent
   *        the chip is POLL AGE and says so in words — a green "42s" over a stale book is the lie
   *        this repo exists to refuse. When `vendor_ts` is present the age is genuine data age and
   *        the caveat disappears by itself, exactly as D-3's root note does.
   *        (This comment previously asserted "NULL on every captured row". That was true on 08-06
   *        and FALSE by 08-07 — the writer fix landed and greek-exposure is 100% populated. A
   *        comment that lies is worse than no comment, so the text now describes the rule, and the
   *        rule reads the data.)
   *
   * Everything drawn here is HYPOTHESIS until E1/E2/E4 rule (law 6).
   */
  import { onMount } from "svelte";
  import {
    buildBookFromStrikes, parseStrikes, resolveDealerWindowCenter, windowAroundSpot,
    type DealerBook, type StrikeGex, type VendorFlip,
  } from "./dealer";
  import {
    fetchDealerScope, FeedError, formatAge, subscribeServedVenueState,
    type DealerLevelsResponse, type DealerResponse, type InstrumentVerdict,
    type VenueState,
  } from "./api";
  import {
    DEFAULT_DEALER_SCOPE, dealerScopeLabel, zeroDteGammaOiForDealer, type DealerScope,
  } from "./dealerScope";
  import { netGexSign, spotVsFlip } from "./dealerRegime";
  import {
    LABEL_FONT_MICRO, LABEL_FONT_PRIMARY, LABEL_FONT_SECONDARY, LABEL_MIN_GAP_PX,
    ladderRowPlacement, measuredSvgWidth, renderedPxToSvgUnits, resolveDealerLabelBaselines,
    type DealerLabelCandidate, type LadderPlacement,
  } from "./layout";
  import { fitClampedLevelLabel } from "./focusLayout";
  import type { LiveMarker } from "./livePriceMarker";
  import PanelFreshness from "./PanelFreshness.svelte";
  import PanelInfo from "./PanelInfo.svelte";
  import AgePill from "./AgePill.svelte";
  import { keptPayloadAfterFailure } from "./dataAge";

  interface Props {
    /** Plan Q2: dealer/GEX budget is 3 minutes. */
    budgetS?: number;
    /** Rows either side of the selected centre. 787 strikes at one row each would be unreadable. */
    rows?: number;
    pollMs?: number;
    /** Parent-owned: §GEX-2 and §STRUCTURE consume one marker from one SPX request. */
    livePrice: LiveMarker;
    /** A cockpit container may dedicate this panel to one timeframe and remove its selector. */
    fixedScope?: DealerScope;
    /** Read-only reuse port for the focus regime footer. */
    onReport?: ((report: InstrumentVerdict | null) => void) | null;
  }
  let {
    budgetS = 180, rows = 25, pollMs = 30_000, livePrice, fixedScope, onReport = null,
  }: Props = $props();

  interface DealerPanelFeed {
    root: string;
    root_verified: boolean;
    vendor_ts: string | null;
    age_s: number | null;
    budget_s: number;
    stale: boolean;
    spot: number | null;
    snapshotUnderlying: number | null;
    strikes: StrikeGex[];
    count: number;
    series: DealerResponse["series"] | undefined;
    expiry: string | null;
    quantity: "gex" | "gamma_oi";
    vendor_flip?: VendorFlip;
    ruledLevels: { callWall: number | null; putWall: number | null; flip: number | null } | null;
    venue_state?: VenueState;
  }

  let scope = $state<DealerScope>(DEFAULT_DEALER_SCOPE);
  let feed = $state<DealerPanelFeed | null>(null);
  let feedFetchedAtMs = $state<number | null>(null);
  let feedRequestKey = $state<string | null>(null);
  let lastServedSpot = $state<number | null>(null);
  let infoOpen = $state(false);
  let relayedVenueState = $state<VenueState | null>(null);

  /**
   * The per-instrument verdict, when the Worker serves one. `series` is the same shape `/api/tide`
   * uses, so both panels apply one rule set rather than drifting apart — that is why the verdict
   * fields were extracted into `InstrumentVerdict` instead of duplicated per envelope.
   */
  let dealerReport = $derived(feed?.series?.[0] ?? null);
  let venueClosed = $derived(
    feed?.venue_state
      ? feed.venue_state.verdict === "CLOSED" ? feed.venue_state : null
      : dealerReport?.venue_state?.verdict === "CLOSED"
        ? dealerReport.venue_state
        : dealerReport?.venue_state
          ? null
          : relayedVenueState?.verdict === "CLOSED" ? relayedVenueState : null,
  );
  $effect(() => onReport?.(dealerReport));
  let error = $state<string | null>(null);
  let detail = $state<string | null>(null);
  let loading = $state(true);
  let mounted = $state(false);

  let book = $derived<DealerBook | null>(feed
    ? (() => {
        const calculated = buildBookFromStrikes(feed.strikes, feed.spot);
        return feed.ruledLevels === null
          ? calculated
          : {
              ...calculated,
              callWall: feed.ruledLevels.callWall,
              putWall: feed.ruledLevels.putWall,
              flip: feed.ruledLevels.flip,
            };
      })()
    : null);
  let windowCenter = $derived(
    feed && book
      ? resolveDealerWindowCenter(feed.spot, {
          snapshotUnderlying: feed.snapshotUnderlying,
          lastServedSpot,
          king: book.king,
        })
      : null,
  );
  let shown = $derived<StrikeGex[]>(book
    ? windowAroundSpot(book.strikes, feed?.spot ?? null, rows, [book.callWall, book.putWall], {
        snapshotUnderlying: feed?.snapshotUnderlying,
        lastServedSpot,
        king: book.king,
      })
    : []);
  let withheld = $derived(book ? book.strikes.length - shown.length : 0);
  let maxAbs = $derived(shown.reduce((m, s) => Math.max(m, Math.abs(s.net)), 0) || 1);
  let freshnessStale = $derived(
    !venueClosed && (dealerReport?.session_verdict === "STALE"
      || (!dealerReport && (feed?.stale === true
        || (feed?.age_s !== null && feed?.age_s !== undefined && feed.age_s > feed.budget_s)))),
  );
  let freshnessTitle = $derived(
    dealerReport
      ? `${dealerReport.symbol} ${dealerReport.session_verdict ?? "NO VERDICT"} · ${dealerReport.session_reason ?? "no reason served"} · dealer data age ${feed?.age_s === null || feed?.age_s === undefined ? "unserved" : `${feed.age_s}s`}`
      : `dealer poll age ${feed?.age_s === null || feed?.age_s === undefined ? "unserved" : formatAge(feed.age_s)}`,
  );

  let requestGeneration = 0;

  function panelFeed(selected: DealerScope, raw: DealerResponse | DealerLevelsResponse): DealerPanelFeed {
    if (selected === "all_expiry") {
      const all = raw as DealerResponse;
      return {
        root: all.root,
        root_verified: all.root_verified,
        vendor_ts: all.vendor_ts,
        age_s: all.age_s,
        budget_s: all.budget_s,
        stale: all.stale,
        spot: all.spot,
        snapshotUnderlying: all.underlying_price ?? null,
        strikes: parseStrikes(all.strikes),
        count: all.count,
        series: all.series,
        expiry: null,
        quantity: "gex",
        vendor_flip: (all as DealerResponse & { vendor_flip?: VendorFlip }).vendor_flip,
        ruledLevels: null,
        venue_state: all.venue_state,
      };
    }

    const zero = raw as DealerLevelsResponse;
    if (!zero.ok) throw new FeedError(zero.reason ?? "0DTE exposure route returned not-ok", 200);
    if (zero.root !== "SPX" || zero.ticker !== "SPX") {
      throw new FeedError(
        `0DTE ladder root refused: root=${zero.root ?? "absent"} ticker=${zero.ticker ?? "absent"}`,
        200,
      );
    }
    return {
      root: zero.root,
      root_verified: true,
      vendor_ts: zero.vendor_time_oldest ?? null,
      age_s: zero.age_s ?? null,
      budget_s: zero.budget_s,
      stale: zero.stale ?? true,
      spot: zero.spot,
      snapshotUnderlying: zero.underlying_price ?? null,
      strikes: zeroDteGammaOiForDealer(zero.strikes),
      count: zero.strikes_total,
      series: undefined,
      expiry: zero.expiry,
      quantity: "gamma_oi",
      vendor_flip: (zero as DealerLevelsResponse & { vendor_flip?: VendorFlip }).vendor_flip,
      ruledLevels: {
        callWall: zero.call_wall,
        putWall: zero.put_wall,
        flip: zero.gamma_flip,
      },
      venue_state: zero.venue_state,
    };
  }

  async function load() {
    const mine = ++requestGeneration;
    const selected = scope;
    const requestKey = selected === "zero_dte" ? "/api/dealer/levels" : "/api/dealer/gex?root=SPX";
    try {
      const raw = await fetchDealerScope(scope);
      if (mine !== requestGeneration || selected !== scope) return;
      feed = panelFeed(selected, raw);
      feedFetchedAtMs = Date.now();
      feedRequestKey = requestKey;
      if (typeof feed.spot === "number" && Number.isFinite(feed.spot) && feed.spot > 0) {
        lastServedSpot = feed.spot;
      }
      error = null;
      detail = null;
    } catch (err) {
      // The panel keeps its last good book and says so, rather than blanking. What it must never do
      // is keep rendering it as if nothing happened — hence the error banner AND the dimming below.
      if (mine === requestGeneration) {
        const fe = err as FeedError;
        error = fe?.message ?? String(err);
        detail = fe?.detail ?? null;
        feed = keptPayloadAfterFailure(feed, feedRequestKey, requestKey);
        if (feed === null) {
          feedFetchedAtMs = null;
          feedRequestKey = null;
        }
      }
    } finally {
      if (mine === requestGeneration) loading = false;
    }
  }

  function setScope(next: DealerScope) {
    if (fixedScope) return;
    if (next === scope) return;
    scope = next;
    // GEX and gamma-OI are different quantities: the previous book may not survive under a new
    // scope label while its replacement loads or refuses.
    feed = null;
    loading = true;
    void load();
  }

  onMount(() => {
    const unsubscribeServedVenueState = subscribeServedVenueState((state) => {
      relayedVenueState = state;
    });
    if (fixedScope) scope = fixedScope;
    load();
    // One authored entrance, once, after the first paint — not on every poll.
    requestAnimationFrame(() => (mounted = true));
    const t = setInterval(load, pollMs);
    return () => {
      clearInterval(t);
      unsubscribeServedVenueState();
    };
  });

  // --- geometry ------------------------------------------------------------------
  const W = 440;
  const ROW_H = 15;
  const PAD_T = 16;
  const PAD_B = 10;
  const GUTTER = 46; // strike labels
  const ZERO = GUTTER + (W - GUTTER) * 0.52;
  let renderedSvgWidth = $state(0);
  let measuredWidth = $derived(measuredSvgWidth(renderedSvgWidth, W));
  let primaryFontSvg = $derived(measuredWidth === null ? 0 : renderedPxToSvgUnits(LABEL_FONT_PRIMARY, W, measuredWidth));
  let secondaryFontSvg = $derived(measuredWidth === null ? 0 : renderedPxToSvgUnits(LABEL_FONT_SECONDARY, W, measuredWidth));
  let microFontSvg = $derived(measuredWidth === null ? 0 : renderedPxToSvgUnits(LABEL_FONT_MICRO, W, measuredWidth));
  let labelGapSvg = $derived(measuredWidth === null ? 0 : renderedPxToSvgUnits(LABEL_MIN_GAP_PX, W, measuredWidth));
  // Reserve a dedicated top text lane. A clamped top level otherwise shares the first ladder row
  // and the primary price tspan becomes unreadable at the standard viewport.
  let clampedTopLabelReserve = $derived(primaryFontSvg + labelGapSvg * 2);

  const y = (i: number) => PAD_T + clampedTopLabelReserve + i * ROW_H;
  const barW = (net: number) => (Math.abs(net) / maxAbs) * ((W - GUTTER) * 0.4);

  /**
   * THE LADDER RENDERS HIGHEST STRIKE AT THE TOP. Owner-reported 2026-08-10, and the instinct was
   * exactly right: it used to render `shown` in array order, which `windowAroundSpot` returns
   * ASCENDING — so 7670 sat at the top and 7830 at the bottom, and **the CALL WALL at +50 pts ABOVE
   * spot drew BELOW the spot line.**
   *
   * The panel immediately to the left is a PRICE CHART with price ascending upward. Two adjacent
   * panels disagreeing about which way price goes is not a preference — at a glance it reads calls
   * as sitting under the market, which is the opposite of the trade.
   *
   * ⚠ THE DATA WAS NEVER WRONG. The footer's distance labels (+50 / +80 / −350 pts) were correct
   * throughout, which is exactly why this survived: every number on the panel agreed with every
   * other number, and the defect lived only in the mapping from index to screen row.
   *
   * `shown` STAYS ASCENDING — it is `windowAroundSpot`'s documented contract and `inWindow`,
   * `maxAbs` and the counts all read it order-independently. Only the DISPLAY order is reversed.
   *
   * ⚠ NAMED `displayRows`, NOT `rows`: `rows` is already the PROP that sets how many strikes the
   * window holds (`windowAroundSpot(..., rows)`). Shadowing it here would have made the ladder ask
   * for `[...shown].reverse()` strikes — an array where a count belongs.
   */
  let displayRows = $derived([...shown].reverse());

  /**
   * Price → y, interpolated across the visible ladder so lines land between bars honestly.
   *
   * ⛔ THIS FUNCTION HAD TO FLIP WITH THE ROWS AND IT IS THE TRAP IN THIS FIX. It mapped the LOWEST
   * strike to the TOP, which was consistent with the old inverted ladder — so reversing the rows
   * alone would have left SPOT and GAMMA FLIP drawing at their mirrored positions, on a ladder that
   * now looked correct. A half-fix here is worse than the defect: the orientation would read right
   * and the two level lines would silently point at the wrong strikes.
   */
  function pricePlacement(p: number): LadderPlacement | null {
    if (shown.length < 2) return null;
    const lo = shown[0].strike;
    const hi = shown[shown.length - 1].strike;
    const plotTop = y(0) + ROW_H / 2;
    const plotHeight = (shown.length - 1) * ROW_H;
    const placement = ladderRowPlacement(p, { loP: lo, hiP: hi }, plotHeight);
    return { ...placement, y: plotTop + placement.y };
  }

  let spotPlacement = $derived(feed?.spot != null ? pricePlacement(feed.spot) : null);
  let livePlacement = $derived(livePrice.kind === "draw" ? pricePlacement(livePrice.price) : null);
  let vendorFlipValue = $derived(feed?.vendor_flip?.value ?? null);
  let netGex = $derived(netGexSign(feed?.strikes ?? []));
  let flipSide = $derived(spotVsFlip(feed?.spot ?? null, vendorFlipValue));
  let vendorFlipState = $derived(feed?.vendor_flip?.state ?? "UNSERVED");
  /** A53: the flip label's second line - the source and, when STALE, the age formatted by formatAge. */
  let flipLine2 = $derived(`UW vol${vendorFlipState === "STALE" ? ` · STALE ${formatAge(feed?.vendor_flip?.age_s ?? Number.NaN)}` : ""}`);
  let vendorFlipSource = $derived(`· ${flipLine2}`);
  let flipPlacement = $derived(vendorFlipValue != null ? pricePlacement(vendorFlipValue) : null);
  let quantityLabel = $derived(feed?.quantity === "gamma_oi" ? "gamma-OI" : "GEX");
  let wallScopeToken = $derived(`${scope === "zero_dte" ? "0DTE" : "ALL"} ${feed?.root ?? "SPX"}`);
  const dealerWallLabel = (name: string, token: string) => `${name} · ${token}`;

  /**
   * The levels a trader actually came for — and the reason this panel does not rely on the ladder
   * alone to show them. The readable window is ~25 strikes; the flip and the walls routinely sit
   * outside it (measured 08-06: spot 7715, window 7655–7775, flip 7805, walls 7400/7900). Drawing
   * only in-window lines meant the panel silently omitted every level it exists to surface.
   * So every level is listed with its distance from spot, and the ones that fall inside the window
   * additionally get their line. Absent is never the same as far away.
   */
  interface Level { name: string; price: number; cls: string; }
  let levels = $derived<Level[]>(
    !book
      ? []
      : ([
          { name: "GAMMA FLIP", price: vendorFlipValue, cls: "fliplbl" },
          { name: "CALL WALL", price: book.callWall, cls: "magnet" },
          { name: "PUT WALL", price: book.putWall, cls: "magnet" },
          { name: `KING · γ-share ${(book.kingShare * 100).toFixed(1)}%`, price: book.king, cls: "magnet" },
        ].filter((l) => l.price !== null) as Level[]),
  );
  /** Same price from two levels is one mark, not two stacked labels. */
  let mergedLevels = $derived(
    levels.reduce<Level[]>((acc, l) => {
      const hit = acc.find((a) => a.price === l.price);
      if (hit) hit.name = `${hit.name} · ${l.name}`;
      else acc.push({ ...l });
      return acc;
    }, []),
  );
  /** A wall or king on the flip's price merges into the flip's mark; its name rides on the flip label. */
  let flipAlsoNames = $derived(
    (mergedLevels.find((l) => l.cls === "fliplbl")?.name ?? "GAMMA FLIP").replace(/^GAMMA FLIP(?: · )?/, ""),
  );
  let inWindow = $derived(new Set(shown.map((s) => s.strike)));
  const levelLabelId = (price: number) => `level:${price}`;
  const primaryLevel = (name: string) => /CALL WALL|PUT WALL|GAMMA FLIP|KING/.test(name);
  const labelPriority = (name: string) =>
    /KING/.test(name) ? 0 : /GAMMA FLIP/.test(name) ? 1 : 2;
  const preferredLabelY = (placement: LadderPlacement) =>
    placement.kind === "clamped" && placement.edge === "top"
      ? placement.y + LABEL_FONT_PRIMARY
      : placement.y - 4;
  let labelCandidates = $derived.by((): DealerLabelCandidate[] => {
    const candidates = mergedLevels
      .filter((level) => primaryLevel(level.name))
      .map((level): DealerLabelCandidate | null => {
        const placement = pricePlacement(level.price);
        return placement
          ? {
              id: levelLabelId(level.price),
              preferredY: preferredLabelY(placement),
              priority: labelPriority(level.name),
              ...(level.cls === "fliplbl" ? { extraBelow: secondaryFontSvg + 2 } : {}),
            }
          : null;
      })
      .filter((candidate): candidate is DealerLabelCandidate => candidate !== null);
    if (livePlacement) candidates.push({ id: "live", preferredY: preferredLabelY(livePlacement) });
    if (spotPlacement) candidates.push({ id: "snapshot", preferredY: preferredLabelY(spotPlacement) });
    return candidates;
  });
  // A53 cure 1 (review P2): the rail is never shorter than its label blocks need, so a sparse ladder
  // with many labels (two-line flip included) grows instead of clipping a label off the viewBox.
  let labelDemandH = $derived(labelCandidates.length === 0
    ? 0
    : primaryFontSvg + (labelCandidates.length - 1) * labelGapSvg
      + labelCandidates.reduce((sum, candidate) => sum + (candidate.extraBelow ?? 0), 0) + 4);
  let H = $derived(Math.max(PAD_T + clampedTopLabelReserve + shown.length * ROW_H + PAD_B, labelDemandH));
  let labelBaselines = $derived(measuredWidth === null
    ? new Map<string, number>()
    : resolveDealerLabelBaselines(
        labelCandidates,
        { minY: primaryFontSvg, maxY: Math.max(primaryFontSvg, H - 4) },
        labelGapSvg,
      ));
  const away = (price: number, spot: number | null | undefined) =>
    spot == null ? "" : `${price > spot ? "+" : "−"}${Math.abs(price - spot).toFixed(0)} pts`;

  let labelMeasureCanvas: HTMLCanvasElement | null = null;
  let labelMeasureFaultLogged = false;
  function measuredLabelWidth(text: string, role: "meta" | "price"): number {
    if (text.length === 0) return 0;
    if (measuredWidth === null || typeof document === "undefined") return Number.POSITIVE_INFINITY;
    labelMeasureCanvas ??= document.createElement("canvas");
    const context = labelMeasureCanvas.getContext("2d");
    if (context === null) {
      if (!labelMeasureFaultLogged) {
        console.error("[DealerRailPanel] FOCUS FIT — no 2d canvas context; clamped labels render their minimum (edge, scope, price) unmeasured");
        labelMeasureFaultLogged = true;
      }
      return Number.POSITIVE_INFINITY;
    }
    const family = getComputedStyle(document.documentElement).getPropertyValue("--font-num").trim() || "monospace";
    const fontSize = role === "price" ? LABEL_FONT_PRIMARY : LABEL_FONT_SECONDARY;
    context.font = `700 ${fontSize}px ${family}`;
    // `.lvl` carries 0.04em tracking. Canvas measureText excludes CSS letter-spacing, so include
    // every inter-glyph interval before converting the rendered pixels into viewBox units.
    const trackedWidth = context.measureText(text).width + Math.max(0, text.length - 1) * fontSize * 0.04;
    return renderedPxToSvgUnits(trackedWidth, W, measuredWidth);
  }

  function fittedClampedLabel(level: Level, placement: Extract<LadderPlacement, { kind: "clamped" }>) {
    return fitClampedLevelLabel({
      edge: `CLAMPED ${placement.edge.toUpperCase()}`,
      name: level.name,
      scope: wallScopeToken,
      price: String(level.price),
      away: away(level.price, feed?.spot),
    }, W - (GUTTER + 2) - 4, measuredLabelWidth);
  }

  // FOCUS-FIT-1 box 5 (RULED 8 (2) R10321): the in-range labels go through the same ladder, no edge mark, so the
  // long served scope yields the name instead of running past the svg with no record.
  const labelBudget = () => W - (GUTTER + 2) - 4;
  // Unmeasurable (server render, no rail width yet) is not unfittable: the in-range labels draw whole, as before.
  const canMeasureLabels = () => measuredWidth !== null && typeof document !== "undefined";
  function fittedLevelLabel(name: string, price: number) {
    return fitClampedLevelLabel({ edge: "", name, scope: wallScopeToken, price: String(price), away: "" },
      labelBudget(), measuredLabelWidth);
  }
  let flipLabel = $derived(!canMeasureLabels() || flipPlacement === null || vendorFlipValue == null ? null
    : fitClampedLevelLabel({
        edge: flipPlacement.kind === "clamped" ? `CLAMPED ${flipPlacement.edge.toUpperCase()}` : "",
        name: "GAMMA FLIP", scope: wallScopeToken, price: String(vendorFlipValue), away: "",
      }, labelBudget(), measuredLabelWidth));
  let flipLine2Text = $derived(`${flipLine2}${flipPlacement?.kind === "clamped" && vendorFlipValue != null ? ` · ${away(vendorFlipValue, feed?.spot)}` : ""}${flipAlsoNames ? ` · ${flipAlsoNames}` : ""}`);
  let flipFits = $derived(flipLabel === null || (flipLabel.fits && measuredLabelWidth(flipLine2Text, "meta") <= labelBudget()));
  const unfitInRangeLevels = $derived(!canMeasureLabels() ? [] : [
    ...(flipFits ? [] : ["GAMMA FLIP"]),
    ...displayRows.flatMap((s) => {
      const lvl = mergedLevels.find((l) => l.price === s.strike && l.cls === "magnet");
      return lvl && !fittedLevelLabel(lvl.name, lvl.price).fits ? [lvl.name] : [];
    }),
  ]);

  // Review r1 01a16dbf P2 (law 1): a clamped label whose minimum still cannot fit is LOUD on the face
  // and logged once per set, never an attribute alone.
  const unfitClampedLevels = $derived(measuredWidth === null ? [] : mergedLevels
    .filter((l) => l.cls === "magnet")
    .flatMap((level) => {
      const placement = pricePlacement(level.price);
      return placement && placement.kind === "clamped" && !fittedClampedLabel(level, placement).fits ? [level.name] : [];
    }));
  let unfitLevels = $derived([...unfitClampedLevels, ...unfitInRangeLevels]);
  let loggedUnfit = "";
  $effect(() => {
    const key = unfitLevels.join(" · ");
    if (key !== "" && key !== loggedUnfit) {
      console.error(`[DealerRailPanel] FOCUS FIT — level label cannot fit its minimum (scope, price): ${key}`);
    }
    loggedUnfit = key;
  });

  const fmt = (n: number) =>
    Math.abs(n) >= 1000 ? `${(n / 1000).toFixed(1)}k` : n.toFixed(1);
</script>

<section class="card" style={`--label-font-primary:${LABEL_FONT_PRIMARY}px;--label-font-secondary:${LABEL_FONT_SECONDARY}px;--label-font-micro:${LABEL_FONT_MICRO}px;${measuredWidth === null ? "" : `--svg-label-font-primary:${primaryFontSvg}px;--svg-label-font-secondary:${secondaryFontSvg}px;--svg-label-font-micro:${microFontSvg}px`}`}>
  <div class="hd">
    <h2>DEALER POSITIONING</h2>
    {#if fixedScope}
      <span class="pill">{dealerScopeLabel(fixedScope)}</span>
    {:else}
      <div class="scope" role="radiogroup" aria-label="Dealer exposure scope">
        {#each (["zero_dte", "all_expiry"] as DealerScope[]) as option}
          <button
            type="button"
            role="radio"
            aria-checked={scope === option}
            class:on={scope === option}
            onclick={() => setScope(option)}>{dealerScopeLabel(option)}</button>
        {/each}
      </div>
    {/if}
    <span
      class="pill chip"
      class:stale={feed?.root_verified === false}
      class:warn={feed?.root_verified === false}
      title={feed?.root_verified === false ? "vendor feed not root-separated (D-3)" : undefined}
    >{feed?.root ?? "ROOT UNSERVED"}</span>
    {#if netGex.kind === "sign"}
      <span class="pill">NET GEX {netGex.sign}</span>
    {:else}
      <span class="chip stale warn">{netGex.reason}</span>
    {/if}
    <span class="pill">{flipSide}</span>
    <!-- PanelFreshness delegates CLOSED rendering to the shared VenueClosedBanner. -->
    <PanelFreshness label="dealer positioning" venueState={venueClosed} stale={freshnessStale} ageTitle={freshnessTitle} />
    <AgePill payload={feed} fetchedAtMs={feedFetchedAtMs} door={feedRequestKey ?? (scope === "zero_dte" ? "/api/dealer/levels" : "/api/dealer/gex?root=SPX")} />
    <span class="chip hyp" data-hyp>HYP</span>
    <PanelInfo id="dealer-positioning-info" label="Dealer positioning information" open={infoOpen} ontoggle={() => (infoOpen = !infoOpen)}>
      <p>root <b>{feed?.root ?? "—"}</b> · {dealerScopeLabel(scope)}{feed?.expiry ? ` · ${feed.expiry}` : ""} · net {quantityLabel} by strike</p>
      {#if venueClosed}<p>last session {venueClosed.last_close?.slice(0, 10) ?? "UNKNOWN"}</p>{/if}
      <p>house cumulative crossing {book?.flip ?? "none"}</p>
      <p><span class="hypo">HYP</span> HYPOTHESIS · E1/E2/E4 pending</p>
      {#if feed && !feed.root_verified}<p>This feed is <b>not root-separated</b> — SPX and SPXW return identical payloads upstream, so no root toggle is offered here. Root-separated OI lives in the OI ladder.</p>{/if}
      {#if windowCenter}
        <p class="readback" role="status">window centre {windowCenter.name} {windowCenter.price}</p>
      {:else}
        <p class="readback" role="status">window centre unavailable</p>
      {/if}
      {#if book && shown.length}
        <p>{shown.length} of {book.strikes.length} strikes · {withheld} outside the window</p>
        <p>{book.regime} · net {fmt(book.netTotal)}</p>
        <p>+{quantityLabel} / −{quantityLabel}: the sign of the vendor's net exposure; no behaviour is claimed</p>
      {/if}
      {#if feed && feed.vendor_ts === null}<p>age is <b>poll age</b> ({feed.age_s === null ? "UNSERVED" : formatAge(feed.age_s)}) — this source carries no vendor timestamp</p>{/if}
    </PanelInfo>
    {#if feed && feed.spot === null && windowCenter}
      <span class="spotfallback" data-window-centre title={`window centre ${windowCenter.name} ${windowCenter.price}`}>
        SPOT ABSENT · centred on {windowCenter.name}
      </span>
    {/if}
  </div>

  <div class="body">
    {#if error && !venueClosed}
      <div class="loud" role="alert">
        <b>FEED ERROR — this panel is not showing live data</b>
        <span>{error}{detail ? ` — ${detail}` : ""}</span>
        <button onclick={load}>Retry</button>
      </div>
    {:else if error && venueClosed}
      <div class="loud" role="status" data-dealer-closed-empty>
        <b>LAST SESSION UNREAD</b>
        <span>{error}{detail ? ` — ${detail}` : ""}</span>
      </div>
    {:else if loading}
      <p class="loading" aria-label="loading dealer book">●</p>
    {/if}

    {#if livePrice.kind === "absent" && !venueClosed}
      <!-- The refusal sits with the snapshot truth even though it draws no line. Omitting this note
           would make "SPX refused" and "the live marker feature does not exist" look identical. -->
      <p class="liveabsent" role="note">LIVE PRICE ABSENT — {livePrice.reason}</p>
    {:else if livePrice.kind !== "absent" && livePlacement === null && !venueClosed}
      <!--
        ⛔⛔ DRAWABLE BUT UNPLACEABLE — A THIRD STATE, AND MY OWN FALSIFIER FOUND IT.
        `pricePlacement` returns null when the ladder has fewer than two rows, so a PERFECTLY GOOD
        live price silently rendered NOTHING: the `absent` note was suppressed (the decision said
        `draw`) and no line existed to replace it. Measured with a fresh SPX fixture:
        `absent-note 0 · live-line 0` — the exact law-1 silence this feature exists to prevent.
        ⇒ *** "THE VALUE IS GOOD" AND "THE VALUE CAN BE SHOWN" ARE DIFFERENT QUESTIONS. ***
        A decision module can only answer the first; the surface must answer the second, out loud.
      -->
      <p class="liveabsent" role="note">
        LIVE PRICE {livePrice.price} · {formatAge(livePrice.ageS)} — NOT PLACED: the dealer ladder
        has no strikes to place it against
      </p>
    {/if}

    {#if vendorFlipValue === null}
      <p class="liveabsent" role="status">GAMMA FLIP {vendorFlipState}{vendorFlipState === "UNSERVED" ? "" : " · UW vol"}</p>
    {:else if flipPlacement === null}
      <p class="liveabsent" role="note">GAMMA FLIP {vendorFlipValue} {vendorFlipSource} — NOT PLACED: the dealer ladder has no strikes to place it against</p>
    {/if}
    {#if unfitLevels.length}
      <span class="chip warn" data-chart-alert data-clamped-label-unfit-chip role="status">LABEL CLIPPED · {unfitLevels.join(" · ")} — the full label is in its tooltip</span>
    {/if}
    <div class="laddermeasure" bind:clientWidth={renderedSvgWidth}>
    {#if measuredWidth === null}
      <span class="chip warn" data-chart-alert>§STRUCTURE · RAIL UNMEASURED — width {renderedSvgWidth}, no draw</span>
    {:else if book && shown.length && !(error && venueClosed)}
      <div class="ladderwrap ladderview" class:dimmed={!!error || feed?.stale} data-dealer-closed-ladder={venueClosed ? "LAST_COMPLETE_SESSION" : undefined}>
        <svg width="100%" viewBox="0 0 {W} {H}" role="img"
             aria-label="Net {quantityLabel} exposure by strike, {shown.length} strikes around {windowCenter?.name ?? 'the available centre'}, highest strike at top">
          <!-- zero rule: the axis every bar is measured from -->
          <line x1={ZERO} x2={ZERO} y1={PAD_T - 6} y2={H - PAD_B} class="zero" />

          {#each displayRows as s, i (s.strike)}
            {@const w = barW(s.net)}
            {@const pos = s.net >= 0}
            <text x={GUTTER - 8} y={y(i) + ROW_H / 2 + 3.5} class="klabel" text-anchor="end">{s.strike}</text>
            <!-- Grows from the zero rule via scaleX, not width: a width transition re-lays-out the
                 whole SVG every frame. Origin is the zero-line edge, so the bar unfurls outward. -->
            <rect
              class="bar {pos ? 'pos' : 'neg'}"
              x={pos ? ZERO : ZERO - w}
              y={y(i) + 2}
              width={w}
              height={ROW_H - 4}
              rx="2"
              style="transform-origin:{pos ? 'left' : 'right'} center; transform:scaleX({mounted ? 1 : 0}); transition-delay:{Math.min(i * 12, 260)}ms"
            ><title>{s.strike} · net {fmt(s.net)} · call {fmt(s.call)} · put {fmt(s.put)}</title></rect>
          {/each}

          <!-- HUD-RAIL-Z-ORDER-1: SVG paints in document order, so EVERY level line is drawn in this pass, before any
               level label below. A later line can no longer cross an earlier label's glyphs (the LIVE line crossed the
               SPOT label). Each block keeps its label's condition verbatim; only the order of painting moved. -->
          {#each mergedLevels.filter((l) => l.cls === "magnet") as level (level.price + '-clampline')}
            {@const placement = pricePlacement(level.price)}
            {#if placement && placement.kind === "clamped"}
              <line x1={GUTTER - 2} x2={W - 4} y1={placement.y} y2={placement.y} class="clampedmark" />
            {/if}
          {/each}
          {#if spotPlacement !== null}
            <line x1={GUTTER - 2} x2={W - 4} y1={spotPlacement.y} y2={spotPlacement.y}
              class:clampedmark={spotPlacement.kind === "clamped"} class:spot={spotPlacement.kind === "at"} />
          {/if}
          {#if livePrice.kind === "draw" && livePlacement !== null}
            <line x1={GUTTER - 2} x2={W - 4} y1={livePlacement.y} y2={livePlacement.y} class="live" />
          {/if}
          {#if flipPlacement !== null && vendorFlipValue != null}
            <line x1={GUTTER - 2} x2={W - 4} y1={flipPlacement.y} y2={flipPlacement.y}
              class:clampedmark={flipPlacement.kind === "clamped"} class:flip={flipPlacement.kind === "at"} />
          {/if}

          <!-- Off-window magnets are pinned to the honest edge and SAY CLAMPED. Omitting them or
               drawing them at an extrapolated y would both claim a price position they do not have. -->
          {#each mergedLevels.filter((l) => l.cls === "magnet") as level (level.price + '-clamp')}
            {@const placement = pricePlacement(level.price)}
            {#if placement && placement.kind === "clamped"}
              {@const label = fittedClampedLabel(level, placement)}
              <text
                x={GUTTER + 2}
                y={primaryLevel(level.name)
                  ? (labelBaselines.get(levelLabelId(level.price)) ?? preferredLabelY(placement))
                  : (placement.edge === "top" ? placement.y + LABEL_FONT_SECONDARY : placement.y - 4)}
                class="lvl clampedlbl"
                class:unfit={!label.fits}
                data-clamped-level={level.name}
                data-clamped-label-variant={label.variant}
                data-clamped-label-fits={label.fits}
              ><title>{label.title}</title>{#if label.showName}<tspan class="clampedmeta">CLAMPED {placement.edge.toUpperCase()} · {dealerWallLabel(level.name, wallScopeToken)}{" "}</tspan>{:else}<tspan class="clampedmeta">CLAMPED {placement.edge.toUpperCase()} · {wallScopeToken}{" "}</tspan>{/if}{#if primaryLevel(level.name)}<tspan class="primaryprice" data-primary-price={level.name}>{level.price}</tspan>{:else}<tspan>{level.price}</tspan>{/if}{#if label.showAway}<tspan class="clampedmeta"> · {away(level.price, feed?.spot)}</tspan>{/if}</text>
            {/if}
          {/each}

          <!-- Levels. Direct-labelled in their own colour: identity is never colour-alone. -->
          {#if spotPlacement !== null}
            <text x={W - 4} y={labelBaselines.get("snapshot") ?? preferredLabelY(spotPlacement)}
              class="lvl spotlbl" class:clampedlbl={spotPlacement.kind === "clamped"} text-anchor="end"
              >{spotPlacement.kind === "clamped" ? `OFF RANGE ${spotPlacement.edge.toUpperCase()} · ` : ""}SPOT {feed?.spot}</text>
          {/if}
          {#if livePrice.kind === "draw" && livePlacement !== null}
            <!-- A SECOND truth, never a replacement: its longer dash and direct LIVE label keep it
                 distinct from the snapshot SPOT line in both themes and without colour alone. -->
            <text x={W - 4}
              y={labelBaselines.get("live") ?? preferredLabelY(livePlacement)}
              class="lvl livelbl" text-anchor="end">
              <tspan class="primaryprice" data-primary-price="LIVE">{livePlacement.kind === "clamped" ? `CLAMPED ${livePlacement.edge.toUpperCase()} · ` : ""}LIVE {livePrice.price}</tspan><tspan class="labelmeta"> · {formatAge(livePrice.ageS)}</tspan>
            </text>
          {/if}
          {#if flipPlacement !== null && vendorFlipValue != null}
            <text x={GUTTER + 2} y={labelBaselines.get(levelLabelId(vendorFlipValue)) ?? preferredLabelY(flipPlacement)}
              class="lvl fliplbl" class:clampedlbl={flipPlacement.kind === "clamped"} class:unfit={!flipFits}
              ><tspan class="labelmeta">{flipPlacement.kind === "clamped" ? `CLAMPED ${flipPlacement.edge.toUpperCase()} · ` : ""}{flipLabel?.showName === false ? wallScopeToken : dealerWallLabel("GAMMA FLIP", wallScopeToken)} </tspan><tspan class="primaryprice" data-primary-price="GAMMA FLIP">{vendorFlipValue}</tspan><tspan class="labelmeta" x={GUTTER + 2} dy={secondaryFontSvg + 2}>{flipLine2}</tspan>{#if flipPlacement.kind === "clamped"}<tspan class="clampedmeta"> · {away(vendorFlipValue, feed?.spot)}</tspan>{/if}{#if flipAlsoNames}<tspan class="labelmeta"> · {flipAlsoNames}</tspan>{/if}{#if flipLabel}<title>{flipLabel.title} · {flipLine2Text}</title>{/if}</text>
          {/if}
          {#each displayRows as s, i (s.strike + '-m')}
            {@const lvl = mergedLevels.find((l) => l.price === s.strike && l.cls === "magnet")}
            {#if lvl}
              {@const label = fittedLevelLabel(lvl.name, lvl.price)}
              <text
                x={GUTTER + 2}
                y={primaryLevel(lvl.name)
                  ? (labelBaselines.get(levelLabelId(lvl.price)) ?? y(i) + ROW_H / 2 + 3.5)
                  : y(i) + ROW_H / 2 + 3.5}
                class="lvl magnet" class:unfit={canMeasureLabels() && !label.fits}
                text-anchor="start"
              ><title>{label.title}</title><tspan class="labelmeta">{#if !canMeasureLabels() || label.showName}{dealerWallLabel(lvl.name, wallScopeToken)}{:else}{wallScopeToken}{/if}</tspan>{#if primaryLevel(lvl.name)}<tspan class="primaryprice" data-primary-price={lvl.name}> {lvl.price}</tspan>{/if}</text>
            {/if}
          {/each}
        </svg>
      </div>

    {:else if !loading && !error && !venueClosed}
      <p class="muted">The dealer book returned no strikes. That is a fault, not an empty panel.</p>
    {/if}
    </div>
  </div>
</section>

<style>
  /* ORDER_HUD_FACE_LEGIBILITY_1 R2: the RAIL UNMEASURED chip's class had no rule here (Svelte scopes styles), so it
     drew unstyled. Copied from SessionCvdPane.svelte:1748 and :1760, not re-derived. */
  .chip { max-width: 100%; box-sizing: border-box; overflow-wrap: anywhere; font-size: 10px; padding: 1px 6px; border-radius: 999px; background: var(--chip); color: var(--muted); }
  .chip.hyp { color: var(--ink); }
  .chip.warn { background: var(--stale-soft); border: 1px solid var(--stale); color: var(--stale-ink); }
  /* THE SERVER-VERDICT CHIP. Same vocabulary as the tide panel and StalenessChip, copied rather
     than re-derived: CLOSED is bordered-and-quiet so it can never be mistaken for a live chip but
     is never amber; only STALE is loud; an unrecognised verdict is dashed ("cannot vouch"). */
  .verdict {
    margin-left: auto;
    font-size: 10px;
    letter-spacing: 0.06em;
    font-weight: 600;
    padding: 2px 7px;
    border-radius: 5px;
    color: var(--muted);
    background: var(--surface-2);
    border: 1px solid var(--line);
    white-space: nowrap;
  }
  .verdict.loud {
    color: var(--stale-ink);
    background: var(--stale-soft);
    border-color: var(--stale);
    font-weight: 700;
  }
  .verdict.unknown { color: var(--ink); background: var(--chip); border-style: dashed; }
  /* Poll age is a claim about OUR process, so it recedes and says so. */
  .pollage {
    font-size: 10px;
    letter-spacing: 0.06em;
    color: var(--faint);
    padding: 2px 4px;
    white-space: nowrap;
  }
  .card {
    background: var(--surface);
    border: 1px solid var(--line);
    border-radius: var(--radius-card);
    box-shadow: var(--shadow);
    display: flex;
    flex-direction: column;
    min-width: 0;
  }
  .hd {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 14px;
    border-bottom: 1px solid var(--line);
    flex-wrap: wrap;
  }
  .hd h2 {
    margin: 0;
    font-size: 12px;
    letter-spacing: 0.12em;
    font-weight: 700;
  }
  .hd .pill { display: inline-flex; align-items: center; border: 1px solid var(--line); border-radius: var(--radius-pill); padding: 2px 7px; background: var(--chip); color: var(--muted); font-size: 10px; line-height: 1; }
  /* The root pill when root_verified is false (D-3): .hd .pill alone out-ranked .chip.warn and inked it neutral. */
  .hd .pill.warn { background: var(--stale-soft); border-color: var(--stale); color: var(--stale-ink); }
  .sub { color: var(--muted); font-size: 11px; }
  .info-toggle {
    min-width: 22px;
    min-height: 22px;
    padding: 0;
    border: 1px solid var(--line);
    border-radius: 50%;
    background: var(--surface-2);
    color: var(--muted);
    cursor: pointer;
  }
  .info-toggle:focus-visible { outline: 2px solid var(--accent); outline-offset: 1px; }
  .readback { color: var(--muted); font: 10px var(--font-num); }
  .scope { display: inline-flex; gap: 2px; padding: 2px; border: 1px solid var(--line);
           border-radius: var(--radius-chip); }
  .scope-pin { padding: 4px 8px; border: 1px solid var(--line); border-radius: var(--radius-chip);
               color: var(--ink); font: 700 9px var(--font-num); letter-spacing: .04em; }
  .scope button { min-height: 24px; padding: 2px 7px; border: 0; border-radius: 3px;
                  background: transparent; color: var(--muted); font: 9px var(--font-num);
                  letter-spacing: .04em; cursor: pointer; }
  .scope button.on { background: var(--chip); color: var(--ink); font-weight: 700; }
  .scope button:focus-visible { outline: 2px solid var(--accent); outline-offset: 1px; }
  .hypo {
    font-size: 9px;
    letter-spacing: 0.08em;
    border-radius: 4px;
    padding: 1px 5px;
    border: 1px solid var(--faint);
    color: var(--muted);
  }
  .body { padding: 10px 14px 14px; min-width: 0; }

  .caveat {
    margin: 0 0 10px;
    font-size: 11px;
    line-height: 1.45;
    color: var(--muted);
    border-left: 1px solid var(--stale);
    padding-left: 10px;
  }
  .caveat b { color: var(--stale-ink); }

  .loud {
    display: flex;
    flex-direction: column;
    gap: 4px;
    align-items: flex-start;
    border: 1px solid var(--stale);
    background: var(--stale-soft);
    color: var(--stale-ink);
    border-radius: var(--radius-chip);
    padding: 10px 12px;
    font-size: 12px;
    margin-bottom: 10px;
  }
  .loud span { color: var(--muted); font-size: 11px; }
  .loud button {
    margin-top: 4px;
    background: var(--surface-2);
    border: 1px solid var(--line);
    color: var(--ink);
    border-radius: 6px;
    padding: 3px 10px;
    font-size: 11px;
    cursor: pointer;
  }
  .loud button:hover { background: var(--chip); }

  .muted { color: var(--muted); font-size: 12px; margin: 0; }

  .liveabsent {
    margin: 0 0 8px;
    color: var(--stale-ink);
    font-family: var(--font-num);
    font-size: 10px;
    line-height: 1.4;
  }
  .spotfallback {
    display: inline-block;
    margin: 0 0 8px;
    padding: 3px 7px;
    border: 1px solid var(--stale);
    border-radius: var(--radius-pill);
    color: var(--stale-ink);
    background: var(--stale-soft);
    font: 700 10px var(--font-num);
    letter-spacing: 0.04em;
  }

  .ladderwrap { transition: opacity 0.2s ease; }
  .ladderview { max-height: 100%; overflow-y: auto; overscroll-behavior: contain; }
  .dimmed { opacity: 0.55; }

  .zero { stroke: var(--faint); stroke-width: 1; }
  .klabel { fill: var(--muted); font-family: var(--font-num); font-size: var(--svg-label-font-micro); }

  .bar {
    transform-box: fill-box;
    transition: transform 620ms cubic-bezier(0.16, 1, 0.3, 1);
  }
  .bar.pos { fill: var(--up); }
  .bar.neg { fill: var(--dn); }
  @media (prefers-reduced-motion: reduce) {
    .bar { transition: none; }
  }

  .spot { stroke: var(--ink); stroke-width: 1; stroke-dasharray: 2 4; opacity: 0.75; }
  /* The snapshot stroke above is untouched. LIVE uses a longer dash, heavier weight and a direct
     label, so the two remain distinguishable without depending on hue. */
  .live { stroke: var(--accent); stroke-width: 1.5; stroke-dasharray: 7 3; }
  .flip { stroke: var(--flip); stroke-width: 1.4; stroke-dasharray: 6 5; }
  .clampedmark { stroke: var(--stale); stroke-width: 2; }
  /* ORDER_HUD_FACE_LEGIBILITY_1 R1: a label is legible over the bars and level lines under it. The surface-coloured
     stroke paints BEHIND the fill (paint-order), so it masks what crosses the glyphs without thickening them. */
  .lvl { font-family: var(--font-num); font-size: var(--svg-label-font-secondary); letter-spacing: 0.04em; paint-order: stroke; stroke: var(--surface); stroke-width: 3.5px; stroke-linejoin: round; }
  .primaryprice { font-size: var(--svg-label-font-primary); font-weight: 700; }
  .labelmeta, .clampedmeta { font-size: var(--svg-label-font-secondary); }
  .spotlbl { fill: var(--muted); }
  .livelbl { fill: var(--accent); font-weight: 700; }
  .fliplbl { fill: var(--flip); }
  .clampedlbl { fill: var(--stale-ink); font-weight: 700; }
  .clampedlbl.unfit, .fliplbl.unfit, .magnet.unfit { fill: var(--age-red-ink); }
  /* (the <dt> variants above use color:, since fill: does not paint HTML text) */
  .magnet { fill: var(--magnet); }

  .levels {
    margin: 10px 0 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
    font-size: 11px;
  }
  .lvlrow {
    display: grid;
    grid-template-columns: 1fr auto auto auto;
    gap: 8px;
    align-items: baseline;
    padding: 2px 0;
  }
  .lvlrow dt {
    font-size: 9px;
    letter-spacing: 0.06em;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .lvlrow dd { margin: 0; }
  .lvlrow .num { font-family: var(--font-num); color: var(--ink); }
  .lvlrow .primarynum { font-size: var(--label-font-primary); font-weight: 700; }
  .lvlrow .dist { font-family: var(--font-num); color: var(--muted); font-size: 10px; }
  .lvlrow .offnote { font-size: 9px; color: var(--faint); letter-spacing: 0.04em; }
  /* Off-ladder levels are quieter, never hidden — the distance is the point. */
  .lvlrow.off .num { color: var(--muted); }
  .lvlrow dt.fliplbl { color: var(--flip); }
  .lvlrow dt.magnet { color: var(--magnet); }

  .lgd {
    display: flex;
    gap: 14px;
    flex-wrap: wrap;
    font-size: 11px;
    color: var(--muted);
    margin-top: 8px;
  }
  .sw {
    display: inline-block;
    width: 14px;
    height: 3px;
    border-radius: 2px;
    vertical-align: middle;
    margin-right: 5px;
  }
  .sw.pos { background: var(--up); }
  .sw.neg { background: var(--dn); }
  .sw.flipsw { background: var(--flip); }

  .foot {
    display: flex;
    gap: 12px;
    align-items: baseline;
    flex-wrap: wrap;
    margin-top: 10px;
    padding-top: 8px;
    border-top: 1px solid var(--line);
    font-size: 11px;
  }
  .foot .num { font-family: var(--font-num); color: var(--ink); }
  .foot .muted { font-size: 10px; }
  .pollnote b { color: var(--stale-ink); }
  .regime {
    font-family: var(--font-num);
    font-weight: 700;
    letter-spacing: 0.06em;
    font-size: 11px;
    border-radius: 5px;
    padding: 2px 8px;
  }
  /* The bare `.regime` — i.e. FLAT — now has a real, deliberately UNCOLOURED appearance rather
     than falling through to whatever `.pos` happened to give it. No direction is claimed. */
  .regime { color: var(--muted); background: var(--chip); }
  .regime.pos { color: var(--up); background: var(--up-soft); }
  .regime.neg { color: var(--dn); background: var(--dn-soft); }
</style>
